import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, RotateCcw, ListChecks } from "lucide-react";

const STEPS = [
  { title: "Set your real budget", detail: "Plan ~7–8% on top of the price: 4% DLD transfer fee, ~2% agency fee, trustee fee (~AED 4,200), plus bank fees if financing." },
  { title: "Get mortgage pre-approval", detail: "If financing, secure bank pre-approval first. Expats typically borrow up to 75–80% LTV on a first home under AED 5M." },
  { title: "Verify your broker", detail: "Check the agent's RERA broker card and the listing's Trakheesi permit on the Dubai REST app — before sharing money or documents." },
  { title: "Choose a freehold community", detail: "Foreign buyers own outright in designated freehold areas: Dubai Marina, Downtown, Palm Jumeirah, Dubai Hills, MBR City and more." },
  { title: "Do your due diligence", detail: "Off-plan: confirm the RERA escrow account (Law No. 8 of 2007) and project status. Ready property: check title deed and service charges." },
  { title: "Sign Form F (the MOU)", detail: "The RERA contract between buyer and seller. It fixes price, timelines and responsibilities — read every clause." },
  { title: "Pay the deposit securely", detail: "Usually 10%, held as a manager's cheque by the broker or trustee until transfer. Never cash to an individual." },
  { title: "Obtain the developer NOC", detail: "The seller secures a No Objection Certificate confirming service charges are settled before transfer." },
  { title: "Transfer at a DLD trustee office", detail: "Pay the 4% DLD fee and trustee fee; the title deed is issued in your name — often the same day." },
  { title: "Set up ownership essentials", detail: "Register Ejari, connect DEWA, understand service charges, and arrange property management if you'll lease it out." },
];

const KEY = "hh-checklist";
const NUDGE_KEY = "hh-checklist-nudged";

export const Checklist = () => {
  const [done, setDone] = useState({});
  const [nudge, setNudge] = useState(false);

  useEffect(() => {
    try {
      setDone(JSON.parse(localStorage.getItem(KEY) || "{}"));
    } catch {}
  }, []);

  const toggle = (i) => {
    const next = { ...done, [i]: !done[i] };
    setDone(next);
    localStorage.setItem(KEY, JSON.stringify(next));
    const firstTick = !done[i] && Object.values(done).filter(Boolean).length === 0;
    if (firstTick && !localStorage.getItem(NUDGE_KEY)) {
      localStorage.setItem(NUDGE_KEY, "1");
      setTimeout(() => setNudge(true), 700);
    }
  };

  const reset = () => {
    setDone({});
    localStorage.removeItem(KEY);
  };

  const count = Object.values(done).filter(Boolean).length;
  const pct = Math.round((count / STEPS.length) * 100);

  return (
    <section id="checklist" data-testid="checklist-section" className="py-20 lg:py-32 scroll-mt-20 bg-ink-card/30 border-y border-white/5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold-light/90 font-medium mb-6">Beginner's checklist</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F8F6F0] leading-tight">
            Ten steps from <span className="italic gold-text">shortlist to title deed.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
            Tick each step as you go — your progress is saved on this device. Every step follows the
            Dubai Land Department and RERA process.
          </p>
        </motion.div>

        <div className="mt-12 mb-10" data-testid="checklist-progress">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.2em] uppercase text-zinc-400 flex items-center gap-2">
              <ListChecks size={14} className="text-gold" /> Your progress
            </span>
            <span className="font-serif text-lg gold-text" data-testid="checklist-progress-label">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-gold to-gold-light"
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>

        <ol className="space-y-3">
          {STEPS.map((s, i) => (
            <motion.li
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(i * 0.05, 0.4), duration: 0.5 }}
            >
              <button
                data-testid={`checklist-item-${i + 1}`}
                onClick={() => toggle(i)}
                className={`w-full text-left flex gap-4 rounded-xl p-5 border transition-all duration-300 ${
                  done[i] ? "border-gold/50 bg-gold/[0.07]" : "gold-ring bg-ink-card/60 hover:bg-ink-hover"
                }`}
              >
                <span
                  className={`mt-0.5 shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-colors ${
                    done[i] ? "bg-gold border-gold text-ink" : "border-zinc-600 text-transparent"
                  }`}
                  data-testid={`checklist-item-checkbox-${i + 1}`}
                >
                  <Check size={14} strokeWidth={3} />
                </span>
                <span>
                  <span className={`block font-serif text-lg ${done[i] ? "text-gold-pale" : "text-[#F8F6F0]"}`}>
                    <span className="text-gold/60 text-sm mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </span>
                  <span className="block mt-1 text-xs sm:text-sm text-zinc-400 leading-relaxed">{s.detail}</span>
                </span>
              </button>
            </motion.li>
          ))}
        </ol>

        <div className="mt-8 text-center">
          <button
            data-testid="checklist-reset-btn"
            onClick={reset}
            className="inline-flex items-center gap-2 text-xs tracking-wide text-zinc-500 hover:text-gold-light transition-colors"
          >
            <RotateCcw size={13} /> Reset checklist
          </button>
        </div>

        {nudge && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm" data-testid="checklist-nudge-dialog">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="w-full max-w-md gold-ring rounded-3xl bg-ink-card p-8 text-center"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-gold-light font-medium">Step one — done</p>
              <h3 className="mt-3 font-serif text-2xl text-[#F8F6F0] leading-snug">
                Nine steps to go. <span className="italic gold-text">Don't walk them alone.</span>
              </h3>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                Form F, the developer NOC and the DLD trustee transfer are exactly where first-time buyers
                slip. Carry out the rest of this checklist with an advisor beside you — a free 30-minute
                Zoom or phone call.
              </p>
              <button
                data-testid="checklist-nudge-book-btn"
                onClick={() => {
                  setNudge(false);
                  document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-6 w-full rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors"
              >
                Book my free call
              </button>
              <button
                data-testid="checklist-nudge-dismiss-btn"
                onClick={() => setNudge(false)}
                className="mt-3 text-xs text-zinc-500 hover:text-gold-light transition-colors"
              >
                I'll continue on my own
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};
